import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom'
import { FormattedMessage } from 'react-intl';
import { Button, Layout, Col, Form, Row, Typography, Spin, message, Table } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { get, isEqual } from 'lodash';
import dayjs from 'dayjs';

import { API } from '../../AxiosWrapper';
import ShippingHistory from './ShippingHistory';
import BottomButtons from './BottomButtons';
import PrintButton from './PrintButton';
import Barcode from 'react-barcode';
import BarcodeRow from './BarcodeRow';
import { useHandleApiError, useFormatMessage, useInterval } from '../shared/hooks'
import ErrorNotFound from '../ErrorNotFound';
import Email from '../shared/delivery/Email'
import Phone from '../shared/delivery/Phone'
import RentalCar from '../shared/delivery/RentalCar'
import InputField from '../shared/delivery/InputField'
import Sender from '../shared/delivery/Sender';
import { DataContext } from "../shared/context"

const { Title, Text } = Typography

const barcodeLabelOptions = {
  height: 55,
  width: 2,
  marginLeft: 8,
  marginRight: 8,
  marginTop: 0,
  marginBottom: 1,
  displayValue: false,
  format: 'CODE128',
};


const AboutDelivery = () => {
  const { senders, branches } = useContext(DataContext)
  const [form] = Form.useForm();
  const handleApiError = useHandleApiError()
  const f = useFormatMessage()
  const { businessKey } = useParams()

  const [editing, setEditing] = useState(false);
  const [data, setData] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false)
  const [posting, setPosting] = useState(false)

  const { Item } = Form
  const getBranchName = (branches, branchId) => {
    const branch = branches.find(b => b.id === branchId)
    return branch?.name || f('Branch.NotDefined')
  }

  const valuesFromData = (data, branches) => ({
    senderId: get(data, 'sender.id'),
    email: get(data, 'recipient.email'),
    phone: get(data, 'recipient.phone'),
    kennitala: get(data, 'recipient.identificationNumber'),
    rentalCar: data.rentalCar,
    createDate: data.createDate ? dayjs(data.createDate).format('YYYY-MM-DD HH:mm') : null,
    createdByUserName: data.createdByUserName,
    recipientName: get(data, 'recipient.name'),
    businessKey: data.businessKey,
    street: get(data, 'recipient.street'),
    senderOrderId: data.senderOrderId,
    description: data.description,
    licensePlate: get(data, 'properties.licensePlate'),
    arrivalTime: get(data, 'properties.arrivalTime'),
    departureTime: get(data, 'properties.departureTime'),
    parking: get(data, 'properties.parking'),
    location: data.location,
    currentState: get(data, 'currentState.name'),
    apexStatus: data.apexStatus,
    branchName: getBranchName(branches, data.deliveryBranchId),
    //NOTE: Bit of a lazy hack
    paymentAmount: get(data, 'amountDue.value') != null ? String(get(data, 'amountDue.value')) : null,
    amountDue: get(data, 'amountDue.value') != null ? String(get(data, 'amountDue.value')) : null,
    paidAmount: get(data, 'paidAmount.value') != null ? String(get(data, 'paidAmount.value')) : null,
  })

  // NOTE: useCallback creates bug (not updating data and creates endless loop)
  const getFormData = (shipNum, branches, oldData) => {
    if (!shipNum || !branches || editing) return
    if (!oldData) setLoading(true) // show loader first time only

    API.GET(`Delivery/${shipNum}`)
      .then(res => {
        if (isEqual(oldData, res.data)) return

        form.setFieldsValue(valuesFromData(res.data, branches))
        setData(res.data)
      })
      .catch(error => {
        handleApiError(error)
        if (error.response?.status === 404)
          setNotFound(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    getFormData(businessKey, branches)
  }, [businessKey, branches]) // eslint-disable-line react-hooks/exhaustive-deps

  // NOTE: setInterval creates closure bug, use this hook instead
  useInterval(() => getFormData(businessKey, branches, data), 2000)

  useEffect(() => {
    if (senders.length === 1)
      form.setFieldValue("senderId", senders[0].id);
  }, [senders, form])

  const handleUpdateConflict = (response) => {
    const { data: errorData } = response
    // Map UpdateBlockReason enum values to translation keys
    // Enum: None=0, AlreadyPaid=1, PaymentInProgress=2, PickupCodeAlreadySent=3,
    //       DeliveryCompleted=4, InLockdown=5, NotShelfDelivery=6, PropertyKeyNotFound=7
    const reasonKeys = {
      1: 'UpdateError.AlreadyPaid',
      3: 'UpdateError.PickupCodeAlreadySent',
      4: 'UpdateError.DeliveryCompleted',
      5: 'UpdateError.InLockdown',
      6: 'UpdateError.NotShelfDelivery',
      7: 'UpdateError.PropertyKeyNotFound',
    }
    const reasonKey = reasonKeys[errorData?.reason]
    if (reasonKey) {
      message.error(f(reasonKey))
    } else if (errorData?.message) {
      message.error(errorData.message)
    } else {
      message.error(f('UpdateError.Conflict'))
    }
  }

  const stateId = get(data, 'currentState.stateId');
  const canEditLocation = editing && stateId === 'customer-picks-up-from-shelf';

  const handlePatch = () => {
    const fieldsToValidate = ['phone', 'email', 'senderId', 'description', 'recipientName', 'paymentAmount', 'licensePlate', 'senderOrderId', 'parking', 'kennitala', 'street', 'arrivalTime', 'departureTime'];
    if (canEditLocation) {
      fieldsToValidate.push('location');
    }
    form.validateFields(fieldsToValidate)
      .then(({ senderId, phone, email, description, recipientName, location, paymentAmount, licensePlate, senderOrderId, parking, kennitala, street, arrivalTime, departureTime }) => {
        // Build update request - only include changed fields
        const updateRequest = {}

        if (senderId !== get(data, 'sender.id')) {
          updateRequest.senderId = senderId
        }
        if (description !== data.description) {
          updateRequest.description = description
        }
        if (recipientName !== get(data, 'recipient.name')) {
          updateRequest.recipientName = recipientName
        }
        if (canEditLocation && location !== data.location) {
          updateRequest.location = location
        }
        // Payment amount - compare as numbers
        const currentAmount = get(data, 'amountDue.value')
        const newAmount = paymentAmount !== undefined && paymentAmount !== null && paymentAmount !== ''
          ? Number(paymentAmount)
          : null
        if (newAmount !== null && newAmount !== currentAmount) {
          updateRequest.paymentAmount = newAmount
        }
        if (email !== get(data, 'recipient.email')) {
          updateRequest.recipientEmail = email
        }
        if (phone !== get(data, 'recipient.phone')) {
          updateRequest.recipientPhone = phone
        }
        if (street !== get(data, 'recipient.street')) {
          updateRequest.recipientStreet = street
        }
        if (kennitala !== get(data, 'recipient.identificationNumber')) {
          updateRequest.recipientKennitala = kennitala?.replace(/\D/g, '')
        }
        if (senderOrderId !== data.senderOrderId) {
          updateRequest.senderOrderId = senderOrderId
        }
        // Properties (e.g., licensePlate, parking, arrivalTime, departureTime)
        const currentLicensePlate = get(data, 'properties.licensePlate')
        if (licensePlate !== currentLicensePlate && licensePlate !== undefined) {
          updateRequest.properties = { ...updateRequest.properties, licensePlate }
        }
        const currentParking = get(data, 'properties.parking')
        if (parking !== currentParking && parking !== undefined) {
          updateRequest.properties = { ...updateRequest.properties, parking }
        }
        const currentArrivalTime = get(data, 'properties.arrivalTime')
        if (arrivalTime !== currentArrivalTime && arrivalTime !== undefined) {
          updateRequest.properties = { ...updateRequest.properties, arrivalTime }
        }
        const currentDepartureTime = get(data, 'properties.departureTime')
        if (departureTime !== currentDepartureTime && departureTime !== undefined) {
          updateRequest.properties = { ...updateRequest.properties, departureTime }
        }

        const promises = []

        // Use new unified update endpoint
        if (Object.keys(updateRequest).length > 0) {
          promises.push(
            API.PATCH('Delivery/' + data.businessKey + '/update', updateRequest)
              .catch(error => {
                if (error.response?.status === 409) {
                  handleUpdateConflict(error.response)
                  throw error // re-throw to prevent success message
                }
                throw error
              })
          )
        }

        if (!promises.length) return

        setPosting(true)
        Promise.all(promises)
          .then(() => {
            setEditing(false)
            message.success(f('Generic.Success'))
          })
          .catch(error => {
            // Only show generic error if not already handled as conflict
            if (error.response?.status !== 409) {
              handleApiError(error)
            }
          })
          .finally(() => setPosting(false))
      })
  }

  const handleCancelEdit = () => {
    form.setFieldsValue(valuesFromData(data, branches))
    setEditing(false)
  }

  const handleCommand = (businessKey, commandName, fields = [], options = {}) => {
    const url = 'Delivery/' + (businessKey ? businessKey : '') + '/' + commandName;
    form.validateFields(fields)
      .then(values => {
        // this is a mess
        const postData = { location: values.shelfLocation };
        if (options.includeBoxSize) {
          postData.boxSize = values.boxSize;
        }
        API.POST(url, postData)
          .then(getFormData(businessKey, branches))
          .catch(handleApiError)
      })
  }

  const delivered = !!data.delivered;

  const title = businessKey
    ? 'AboutDelivery.Title'
    : data?.orderType === 'collection'
      ? 'CreateDelivery.CollectionTitle'
      : 'CreateDelivery.DeliveryTitle'

  const validateMessages = {
    required: f("Validation.Default.Required"),
    whitespace: f("CreateDelivery.FieldWhitespaceError"),
  }

  // Compute barcode to show (and print) — gating decided server-side via data.canPrintLabel
  let barcodeValue = null;
  let qrCodeVal = null;
  let out = false;
  if (!delivered && data.canPrintLabel) {
    if (data.orderType === 'collection' && data.pickupCode) {
      // Collection: staff takes keys out of box — show pickup code
      barcodeValue = data.pickupCode;
      qrCodeVal = data.pickupCodeUrl;
      out = true;
    } else if (stateId === 'order-placed-in-box' && data.deliveryCode) {
      // Delivery: customer picks up package — show delivery code
      barcodeValue = data.deliveryCode;
      qrCodeVal = data.deliveryCodeUrl;
      out = false;
    } else if (data.reclaimCode) {
      // Delivery reclaim: staff retrieves package — show reclaim code
      barcodeValue = data.reclaimCode;
      qrCodeVal = data.reclaimCodeUrl;
      out = true;
    }
  }

  const sameOrderKeys = Array.isArray(data.sameOrderIdBusinessKeys) ? data.sameOrderIdBusinessKeys : [];
  const sameOrderData = sameOrderKeys.map(bk => ({ key: bk, businessKey: bk }));
  const sameOrderColumns = [
    {
      dataIndex: 'businessKey',
      title: f('DeliveryColumn.businessKey'),
      render: (bk) => (
        <Link to={`/about-delivery/${bk}`}>{bk}</Link>
      ),
      width: 200,
    }
  ];

  if (loading) return <Spin size="large" />
  if (notFound) return (<ErrorNotFound />)

  return (
    <Layout.Content>
      <Title level={2}><FormattedMessage id={title} /></Title>
      <Row>
        <Col xs={24} xl={12}>
          <Form
            form={form}
            layout="vertical"
            className="form-style"
            onFinish={handlePatch}
            validateMessages={validateMessages}
          >
            <InputField name="createDate" disabled={editing} readOnly={true} />
            <InputField name="createdByUserName" disabled={editing} readOnly={true} />
            <InputField name="businessKey" disabled={editing} readOnly={true} />
            <Sender orderType={data.orderType} editing={editing} />
            <InputField name="recipientName" readOnly={!editing} />
            <InputField name="kennitala" readOnly={!editing} />
            <InputField name="street" readOnly={!editing} />
            <Email readOnly={!editing} />
            <Phone readOnly={!editing} />
            <InputField name="senderOrderId" readOnly={!editing} />
            <InputField name="description" readOnly={!editing} />
            <InputField name="licensePlate" readOnly={!editing} />
            <InputField name="arrivalTime" readOnly={!editing} />
            <InputField name="departureTime" readOnly={!editing} />
            <InputField name="parking" readOnly={!editing} />
            <RentalCar disabled={true} />
            <InputField name="currentState" disabled={editing} readOnly={true} />
            <InputField name="location" readOnly={!canEditLocation} disabled={!canEditLocation} />
            <InputField name="apexStatus" disabled={editing} readOnly={true} />
            {/* only show branch when user has access to multiple branches */}
            {branches.length > 1 && <InputField name="branchName" disabled={editing} readOnly={true} />}
            <InputField name="paymentAmount" readOnly={!editing} />
            <InputField name="amountDue" disabled={true} readOnly={true} />
            {get(data, 'paidAmount.value') != null && (
              <Form.Item label={<FormattedMessage id="Delivery.paidAmount.label" />}>
                <div>
                  <CheckCircleFilled style={{ color: '#52c41a', marginRight: 6 }} />
                  <Text strong>{get(data, 'paidAmount.value')} {get(data, 'paidAmount.currency', 'ISK')}</Text>
                  {data.externalPaymentId && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <FormattedMessage id="Delivery.externalPaymentId.label" />: {data.externalPaymentId}
                      </Text>
                    </div>
                  )}
                </div>
              </Form.Item>
            )}

            {editing ? (
              <div style={{ display: 'flex', gap: 12 }}>
                <Button shape="round" type="primary" htmlType="submit" loading={posting}>
                  <FormattedMessage id="AboutDelivery.Save" />
                </Button>
                <Button shape="round" type="default" onClick={handleCancelEdit} disabled={posting}>
                  <FormattedMessage id="CreateDelivery.Cancel" />
                </Button>
              </div>
            ) : (
              <>
                <Item name="edit">
                  <Button shape="round" type="primary" onClick={() => setEditing(true)}>
                    <FormattedMessage id="AboutDelivery.ChangeInformation" />
                  </Button>
                </Item>
                <BottomButtons
                  commands={data.commands ?? []}
                  handleSubmit={(command, fields, options) => handleCommand(data.businessKey, command, fields, options)}
                  branchId={data.deliveryBranchId}
                />
              </>
            )}
          </Form>
        </Col>
        <Col xs={24} xl={10}>
          {/* Group 1: Shipping history */}
          <ShippingHistory data={data} loading={loading} />

          {/* Group 2: Barcode + Print */}
          {barcodeValue && (
            <div style={{ marginBottom: 24 }}>
              <BarcodeRow barCodeVal={barcodeValue} qrCodeVal={qrCodeVal} out={out} />
              <div style={{ marginTop: 8 }}>
                <PrintButton businessKey={data.businessKey}>
                  {data.recipient?.identificationNumber}
                  <div>
                    <Barcode className="BarcodeOutBox" {...barcodeLabelOptions} value={barcodeValue} />
                  </div>
                </PrintButton>
              </div>
            </div>
          )}

          {/* Group 3: Related shipment + same order table */}
          {(data.relatedShipmentBusinessKey || sameOrderKeys.length > 0) && (
            <div>
              {data.relatedShipmentBusinessKey && (
                <div style={{ marginBottom: 8 }}>
                  <Text><FormattedMessage id="AboutDelivery.RelatedShipment" /> </Text>
                  <Link to={`/about-delivery/${data.relatedShipmentBusinessKey}`}>
                    {data.relatedShipmentBusinessKey}
                  </Link>
                </div>
              )}
              {sameOrderKeys.length > 0 && (
                <div>
                  <Title level={4} style={{ marginTop: 8 }}>
                    <FormattedMessage id="AboutDelivery.SameOrder" />
                  </Title>
                  <Table
                    className="small-table"
                    columns={sameOrderColumns}
                    dataSource={sameOrderData}
                    pagination={false}
                    size="small"
                    showHeader={false}
                  />
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
    </Layout.Content >
  );
}

export default AboutDelivery;
